import React, { useState, useEffect, useContext } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaBox,
  FaEye,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const StockSummaryReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass = "bg-gray-200 hover:bg-gray-300 text-gray-800";

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "";
  const storedBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // common headers
  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": storedBranchId,
    "x-branch": storedBranchType,
    "x-userid": userId,
  };

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // filters
  const [branchId, setBranchId] = useState(storedBranchId || "8");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [level, setLevel] = useState("Select");
  const [stock, setStock] = useState("Select");

  // dropdown lists
  const [branchList, setBranchList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [innerSubCategoryList, setInnerSubCategoryList] = useState([]);
  const [deepInnerSubCategoryList, setDeepInnerSubCategoryList] = useState([]);

  // selected dropdown ids
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedInnerSubCategory, setSelectedInnerSubCategory] = useState("");
  const [selectedDeepInnerSubCategory, setSelectedDeepInnerSubCategory] =
    useState("");

  const [data, setData] = useState([]);

  // fetch branches + categories on load
  useEffect(() => {
    setLoading(true);
    axios
      .get("https://nissi-india.com/api/fillddl_itemRpt", {
        params: { branchid: branchId || storedBranchId || 8 },
        headers: commonHeaders,
      })
      .then((res) => {
        setBranchList(res.data.branchList || []);
        setCategoryList(res.data.categoryList || []);
      })
      .catch((err) => {
        console.error("Initial dropdown fetch failed", err);
        toast.error("Failed to load branches/categories");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // fetch categories when branch changes
  useEffect(() => {
    if (!branchId) return;

    setLoading(true);
    axios
      .get("https://nissi-india.com/api/fillddl_itemRpt", {
        params: { branchid: branchId },
        headers: commonHeaders,
      })
      .then((res) => {
        setBranchList(res.data.branchList || []);
        setCategoryList(res.data.categoryList || []);
      })
      .catch((err) => {
        console.error("Branch/category fetch failed", err);
        toast.error("Failed to load categories");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [branchId]);

  // fetch sub categories
  const fetchSubCategories = (categoryId) => {
    if (!categoryId) {
      setSubCategoryList([]);
      setInnerSubCategoryList([]);
      setDeepInnerSubCategoryList([]);
      setSelectedSubCategory("");
      setSelectedInnerSubCategory("");
      setSelectedDeepInnerSubCategory("");
      return;
    }

    axios
      .get(`https://nissi-india.com/api/subcategory_rpt/${categoryId}`, {
        headers: commonHeaders,
      })
      .then((res) => {
        setSubCategoryList(res.data.subCategoryList || []);
        setInnerSubCategoryList([]);
        setDeepInnerSubCategoryList([]);
        setSelectedSubCategory("");
        setSelectedInnerSubCategory("");
        setSelectedDeepInnerSubCategory("");
      })
      .catch((err) => {
        console.error("Subcategory fetch failed", err);
        toast.error("Failed to load sub categories");
      });
  };

  // fetch inner sub categories
  const fetchInnerSubCategories = (subCategoryId) => {
    if (!subCategoryId) {
      setInnerSubCategoryList([]);
      setDeepInnerSubCategoryList([]);
      setSelectedInnerSubCategory("");
      setSelectedDeepInnerSubCategory("");
      return;
    }

    axios
      .get(`https://nissi-india.com/api/innersubcategory_rpt/${subCategoryId}`, {
        headers: commonHeaders,
      })
      .then((res) => {
        setInnerSubCategoryList(res.data.innerSubCategoryList || []);
        setDeepInnerSubCategoryList([]);
        setSelectedInnerSubCategory("");
        setSelectedDeepInnerSubCategory("");
      })
      .catch((err) => {
        console.error("Inner subcategory fetch failed", err);
        toast.error("Failed to load inner sub categories");
      });
  };

  // fetch deep inner sub categories
  const fetchDeepInnerSubCategories = (innerSubCategoryId) => {
    if (!innerSubCategoryId) {
      setDeepInnerSubCategoryList([]);
      setSelectedDeepInnerSubCategory("");
      return;
    }

    axios
      .get(
        `https://nissi-india.com/api/deepinnersubcategory_rpt/${innerSubCategoryId}`,
        {
          headers: commonHeaders,
        }
      )
      .then((res) => {
        setDeepInnerSubCategoryList(res.data.deepInnerSubCategoryList || []);
      })
      .catch((err) => {
        console.error("Deep inner subcategory fetch failed", err);
        toast.error("Failed to load deep inner sub categories");
      });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const handleSearch = () => {
    setLoading(true);

    const params = {
      sessionBranchId: branchId,
      ddl_Branch: String(branchId || "Select"),
      ddl_category: selectedCategory || "Select",
      ddl_subCategory: selectedSubCategory || "Select",
      ddl_innersubcategory: selectedInnerSubCategory || "Select",
      ddl_deepinnersubcategory: selectedDeepInnerSubCategory || "Select",
      txt_item: itemCode || "",
      txt_itemname: itemName || "",
      txt_FromDate: formatDate(fromDate) || "",
      txt_ToDate: formatDate(toDate) || "",
      ddl_level: level || "Select",
      ddl_interval: "Select",
      ddl_Stockwise: stock || "Select",
      financeyearstartdate:
        localStorage.getItem("financeyearstartdate") || "",
    };

    axios
      .post("https://nissi-india.com/api/stock_summaryRpt", params, {
        headers: commonHeaders,
      })
      .then((res) => {
        const rows = res.data.rows || res.data.recordset || res.data.recordsets?.[0] || [];
        setData(rows);
        toast.success(`Loaded ${rows.length} records`);
      })
      .catch((err) => {
        console.error("Stock summary fetch failed", err);
        toast.error("Failed to fetch stock summary");
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePrint = () => {
    const params = {
      sessionBranchId: branchId,
      ddl_Branch: String(branchId || "Select"),
      ddl_category: selectedCategory || "Select",
      ddl_subCategory: selectedSubCategory || "Select",
      ddl_innersubcategory: selectedInnerSubCategory || "Select",
      ddl_deepinnersubcategory: selectedDeepInnerSubCategory || "Select",
      txt_item: itemCode || "",
      txt_itemname: itemName || "",
      txt_FromDate: formatDate(fromDate) || "",
      txt_ToDate: formatDate(toDate) || "",
      ddl_level: level || "Select",
      ddl_interval: "Select",
      ddl_Stockwise: stock || "Select",
      financeyearstartdate:
        localStorage.getItem("financeyearstartdate") || "",
    };

    const query = new URLSearchParams(params).toString();
    window.open(`/inventory/StockSummaryReportPrint?${query}`, "_blank");
  };

  const clearFilters = () => {
    setBranchId(storedBranchId || "8");
    setFromDate("");
    setToDate("");
    setItemCode("");
    setItemName("");
    setLevel("Select");
    setStock("Select");
    setSelectedCategory("");
    setSelectedSubCategory("");
    setSelectedInnerSubCategory("");
    setSelectedDeepInnerSubCategory("");
    setSubCategoryList([]);
    setInnerSubCategoryList([]);
    setDeepInnerSubCategoryList([]);
    setData([]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatNumber = (value) => {
    return value
      ? parseFloat(value).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="STOCK SUMMARY REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">Stock Summary Report</h1>
            <p className="text-sm text-gray-500 mt-1">
              Comprehensive overview of inventory stock levels and values
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Item Code</label>
                <input
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  placeholder="Enter Item Code"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Item Name</label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="Enter Item Name"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Level</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                >
                  <option value="Select">Select</option>
                  <option value="high">High</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Stock</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                >
                  <option value="Select">Select</option>
                  <option value="Zero">Zero Stock</option>
                  <option value="NonZero">Non Zero Stock</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Branch</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  <option value="">Select Branch</option>
                  {branchList.map((b) => (
                    <option
                      key={b.fld_Branchid || b.id}
                      value={b.fld_Branchid || b.id}
                    >
                      {b.fld_BranchName || b.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Category
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    fetchSubCategories(e.target.value);
                  }}
                >
                  <option value="">Select Category</option>
                  {categoryList.map((c) => (
                    <option
                      key={c.fld_itemcategoryid || c.id}
                      value={c.fld_itemcategoryid || c.id}
                    >
                      {c.fld_itemcategory_name || c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Sub Category
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={selectedSubCategory}
                  onChange={(e) => {
                    setSelectedSubCategory(e.target.value);
                    fetchInnerSubCategories(e.target.value);
                  }}
                >
                  <option value="">Select Sub Category</option>
                  {subCategoryList.map((sc) => (
                    <option
                      key={sc.fld_itemsubCategoryid}
                      value={sc.fld_itemsubCategoryid}
                    >
                      {sc.fld_itemsubCategoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Inner Sub Category
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={selectedInnerSubCategory}
                  onChange={(e) => {
                    setSelectedInnerSubCategory(e.target.value);
                    fetchDeepInnerSubCategories(e.target.value);
                  }}
                >
                  <option value="">Select Inner Sub Category</option>
                  {innerSubCategoryList.map((isc) => (
                    <option
                      key={isc.fld_innersubCategoryid}
                      value={isc.fld_innersubCategoryid}
                    >
                      {isc.fld_innersubCategoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Deep Inner Sub Category
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white border-gray-300"
                  value={selectedDeepInnerSubCategory}
                  onChange={(e) => setSelectedDeepInnerSubCategory(e.target.value)}
                >
                  <option value="">Select Deep Inner Sub Category</option>
                  {deepInnerSubCategoryList.map((disc) => (
                    <option
                      key={disc.fld_deepinnersubCategoryid}
                      value={disc.fld_deepinnersubCategoryid}
                    >
                      {disc.fld_deepinnersubCategoryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                <FaPrint className="w-4 h-4" />
                Print Report
              </button>
            </div>
          </div>
        )}

        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} records)`}
            </h2>

            {data.length > 0 && (
              <span className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      S No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      UOM
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Closing Stock
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Report
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((po, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {po.fld_branchName || po.fld_branchname}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {po.fld_itemname}
                      </td>
                      <td
                        className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                        onClick={() => {
                          window.open(
                            `/inventory/StockItemDetails?id=${po.fld_itemid}`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      >
                        {po.fld_itemcode}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {po.fld_location}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {po.fld_uomname}
                      </td>
                      <td
                        className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                        onClick={() => {
                          window.open(
                            `/inventory/PriceStockDetails?id=${po.fld_itemid}`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      >
                        {formatCurrency(po.Price1 || po.price1)}
                      </td>
                      <td
                        className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                        onClick={() => {
                          window.open(
                            `/inventory/ItemStockDetails?id=${po.fld_itemid}`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                      >
                        {formatNumber(po.ClosingStock || po.closingStock)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(po.Amount || po.amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            window.open(
                              `/inventory/ItemTransactionReport?id=${po.fld_itemid}`,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                          className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                          title="View Transaction Report"
                        >
                          <FaEye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <FaBox className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No stock records found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default StockSummaryReport;